import { type ChangeEvent, useEffect, useState, type ReactNode  } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Stack } from '@mui/material';

import { partnerLinkPatch } from '@/entities/partner';
import { Dialog } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/lib/Button';
import { TextField } from '@/shared/ui/lib/TextField';

interface RenameLinkDialogProps {
  open: boolean;
  linkId: string | null;
  initialName: string;
  onClose: () => void;
}

export function RenameLinkDialog({
  open,
  linkId,
  initialName,
  onClose,
}: RenameLinkDialogProps): ReactNode {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState(initialName);

  useEffect(() => {
    setName(initialName);
  }, [initialName, open]);

  const patch = useMutation({
    ...partnerLinkPatch(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['partner', 'links'] });
      onClose();
    },
  });

  const isValid =
    !!linkId && name.trim().length > 0 && name.trim().length <= 120 && name.trim() !== initialName;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('common:affiliatePage.dialogs.renameLink.title') ?? ''}
      leftButtonSlot={
        <Button onClick={onClose} disabled={patch.isPending}>
          {t('common:buttons.cancel')}
        </Button>
      }
      rightButtonSlot={
        <Button
          variant="contained"
          disabled={!isValid || patch.isPending}
          onClick={() => {
            if (!linkId) return;
            patch.mutate({ id: linkId, data: { name: name.trim() } });
          }}
        >
          {t('common:buttons.save')}
        </Button>
      }
    >
      <Stack spacing={2} sx={{ minWidth: 360 }}>
        <TextField
          label={t('common:affiliatePage.dialogs.createLink.nameLabel')}
          value={name}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
          fullWidth
          autoFocus
        />
      </Stack>
    </Dialog>
  );
}
